import React, { useState, useEffect, useRef } from 'react';
import {
  Smartphone, Laptop, Tablet, Watch, Gamepad2, Glasses, ScanEye,
  HelpCircle, Check, ArrowDown, ChevronLeft, Bot, Sparkles, RotateCw,
} from 'lucide-react';
import { getDeviceImage } from '../../shared/deviceImages';
import { fetchJsonWithRetry } from '../../shared/resilientFetch';
import { getBrandsByCategory, getModelsByBrand } from '../../data/sellDeviceData';

/*
 * SellGuideLayer — 100% presentational "hand-holding" guidance for the
 * Instant Estimate tool. It reads state passed down as props and calls the
 * SAME setQuery / handleGetEstimate handlers the tool already uses. It does
 * NOT change any pricing, search, or submission logic — appearance only.
 *
 * The DevicePicker pulls the SAME categories → brands → models the MANUAL
 * category flow uses (catalog API), so the two flows always stay in sync.
 */

const API_URL = (() => { const envUrl = process.env.REACT_APP_BACKEND_URL; if (!envUrl || envUrl === '') return window.location.origin; try { const envHost = new URL(envUrl).hostname; const currentHost = window.location.hostname; if (currentHost === 'localhost' || currentHost === '127.0.0.1' || envHost === currentHost || currentHost.includes('preview.emergentagent.com')) { return envUrl; } return window.location.origin; } catch (e) { return window.location.origin; } })();

// Icon + accent color per catalog category (keyed by sell slug).
const CAT_ICON = { Smartphone, Laptop, Tablet, Watch, Gamepad2, Glasses, ScanEye, HelpCircle };
const CAT_COLOR = {
  'phones-sell': '#06b6d4',
  'tablets-sell': '#6366f1',
  'laptops-sell': '#8b5cf6',
  'watches-sell': '#f59e0b',
  'consoles-sell': '#10b981',
  'meta-quest-sell': '#0ea5e9',
  'meta-glasses-sell': '#0284c7',
  other: '#64748b',
};
const catLabel = (name) => (name || '').replace(/^sell your\s*/i, '').trim() || 'Device';

// Always-available fallback so the picker NEVER collapses to just "Other"
// (e.g. if the categories API is slow, errors, or the current env's catalog
// is empty). These mirror the manual flow's standard sell categories.
const FALLBACK_CATS = [
  { slug: 'phones-sell', name: 'Sell Your Phone', icon: 'Smartphone' },
  { slug: 'tablets-sell', name: 'Sell Your Tablet', icon: 'Tablet' },
  { slug: 'laptops-sell', name: 'Sell Your Laptop', icon: 'Laptop' },
  { slug: 'watches-sell', name: 'Sell Your Watch', icon: 'Watch' },
  { slug: 'consoles-sell', name: 'Sell Your Console', icon: 'Gamepad2' },
];
const OTHER_CAT = { slug: 'other', name: 'Other Devices', icon: 'HelpCircle', other: true };

// Meta Devices — priced via the live eBay formula (not the price book).
// Split into TWO folders: VR headsets (Quest) and smart glasses (Ray-Ban / Oakley Meta).
// Tapping either shows its curated model list directly (no catalog brands step).
const META_QUEST_CAT = { slug: 'meta-quest-sell', name: 'Sell Your Meta Quest', icon: 'ScanEye', meta: true, metaKind: 'quest' };
const META_GLASSES_CAT = { slug: 'meta-glasses-sell', name: 'Sell Your Meta Glasses', icon: 'Glasses', meta: true, metaKind: 'glasses' };
const META_QUEST_MODELS = [
  { name: 'Meta Quest 3' },
  { name: 'Meta Quest 3S' },
  { name: 'Meta Quest 2' },
  { name: 'Meta Quest Pro' },
];
const META_GLASSES_MODELS = [
  { name: 'Ray-Ban Meta (Gen 3)' },
  { name: 'Ray-Ban Meta (Gen 2)' },
  { name: 'Ray-Ban Meta Wayfarer' },
  { name: 'Ray-Ban Meta Headliner' },
  { name: 'Ray-Ban Meta Skyler' },
  { name: 'Ray-Ban Meta Display' },
  { name: 'Oakley Meta HSTN' },
  { name: 'Oakley Meta Vanguard' },
  { name: 'Ray-Ban Stories' },
];
const META_CATS = [META_QUEST_CAT, META_GLASSES_CAT];

// Static device catalog fallbacks (bundled) so the picker NEVER shows a blank grid
// + "didn't load" retry if the live catalog API has a transient blip. Tapping a model
// only fills the search box, so the downstream estimate still resolves server-side by name.
const fallbackBrands = (catSlug) => {
  try {
    const id = (catSlug || '').replace('-sell', '');
    return (getBrandsByCategory(id) || []).map(b => ({ slug: b.id, name: b.name }));
  } catch (e) { return []; }
};
const fallbackModels = (brandSlug) => {
  try {
    return (getModelsByBrand(brandSlug) || []).map(m => ({ name: m.name, year: m.year }));
  } catch (e) { return []; }
};

// Brand → simpleicons slug (rendered in the brand's official color from cdn.simpleicons.org).
// Unknown brands fall back to a colored letter avatar so the row never looks broken.
const BRAND_SLUG = {
  apple: 'apple', samsung: 'samsung', sony: 'sony', google: 'google',
  microsoft: 'microsoft', oneplus: 'oneplus', motorola: 'motorola',
  xiaomi: 'xiaomi', huawei: 'huawei', nintendo: 'nintendo', dell: 'dell',
  hp: 'hp', 'hewlettpackard': 'hp', lenovo: 'lenovo', asus: 'asus',
  acer: 'acer', nvidia: 'nvidia', nokia: 'nokia', oppo: 'oppo', vivo: 'vivo',
  nothing: 'nothing', razer: 'razer', lg: 'lg', tcl: 'tcl', realme: 'realme',
  honor: 'honor', microsoftsurface: 'microsoft', surface: 'microsoft',
  playstation: 'playstation', xbox: 'xbox', valve: 'valve', steamdeck: 'steam',
};
const normBrand = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

function BrandLogo({ name, color }) {
  const key = normBrand(name);
  const matchKey = Object.keys(BRAND_SLUG).find(k => key === k || key.includes(k));
  const [failed, setFailed] = useState(false);
  if (matchKey && !failed) {
    return (
      <img
        src={`https://cdn.simpleicons.org/${BRAND_SLUG[matchKey]}`}
        alt=""
        aria-hidden="true"
        className="sg-brand-logo"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className="sg-brand-letter" style={{ background: color || '#10b981' }}>
      {(name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}

// ─── Numbered step progress bar (#1 + #5 checkmarks) ───
export function GuideProgress({ step }) {
  const steps = [
    { n: 1, label: 'Your Device' },
    { n: 2, label: 'Condition' },
    { n: 3, label: 'Cash Offer' },
  ];
  return (
    <div className="sg-progress" data-testid="guide-progress">
      {steps.map((s, i) => {
        const done = step > s.n;
        const active = step === s.n;
        return (
          <React.Fragment key={s.n}>
            <div className={`sg-step ${active ? 'sg-step-active' : ''} ${done ? 'sg-step-done' : ''}`}>
              <span className="sg-step-num">
                {done ? <Check className="w-3.5 h-3.5" /> : s.n}
              </span>
              <span className="sg-step-label">{s.label}</span>
            </div>
            {i < steps.length - 1 && <span className={`sg-connector ${step > s.n ? 'sg-connector-done' : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Friendly robot coach with speech bubble (#3 + #7 next-step text) ───
export function GuideCoach({ message }) {
  if (!message) return null;
  return (
    <div className="sg-coach" data-testid="guide-coach">
      <div className="sg-coach-avatar">
        <Bot className="w-5 h-5 text-white" />
      </div>
      <div className="sg-coach-bubble" key={message}>
        {message}
      </div>
    </div>
  );
}

// ─── Bouncing "point-here" arrow with caption (#2) ───
export function GuidePointer({ label, testId = 'guide-pointer' }) {
  if (!label) return null;
  return (
    <div className="sg-pointer" data-testid={testId}>
      <span className="sg-pointer-text">{label}</span>
      <ArrowDown className="w-5 h-5 sg-pointer-arrow" />
    </div>
  );
}

// ─── Catalog-driven tap-first picker (#4) — SAME categories/brands/models
// as the manual /sell category flow (products/categories → products/brands →
// Apple sub-lines for the Instant Estimate picker (Tablets → iPad lines,
// Laptops → MacBook lines) + Samsung phones (Galaxy S / Z Flip / Z Fold).
// Returns null for every other category/brand.
function getSubCats(cat, brand) {
  const catLc = (cat?.slug || cat?.name || '').toLowerCase();
  const bLc = ((brand?.slug || '') + ' ' + (brand?.name || '')).toLowerCase();
  const isApple = /apple|ipad|macbook/.test(bLc);
  const isSamsung = /samsung|galaxy/.test(bLc);
  if (isApple && catLc.includes('tablet')) {
    return [
      { key: 'ipad-pro', label: 'iPad Pro', img: getDeviceImage('iPad Pro', 'tablets'), test: (n) => /ipad\s*pro/.test(n) },
      { key: 'ipad-air', label: 'iPad Air', img: getDeviceImage('iPad Air', 'tablets'), test: (n) => /ipad\s*air/.test(n) },
      { key: 'ipad-std', label: 'iPad', img: getDeviceImage('iPad', 'tablets'), test: (n) => /ipad/.test(n) && !/pro|air|mini/.test(n) },
      { key: 'ipad-mini', label: 'iPad mini', img: getDeviceImage('iPad mini', 'tablets'), test: (n) => /ipad\s*mini/.test(n) },
    ];
  }
  if (isApple && catLc.includes('laptop')) {
    return [
      { key: 'mba', label: 'MacBook Air', img: getDeviceImage('MacBook Air', 'laptops'), test: (n) => /macbook\s*air/.test(n) },
      { key: 'mbp', label: 'MacBook Pro', img: getDeviceImage('MacBook Pro', 'laptops'), test: (n) => /macbook\s*pro/.test(n) },
    ];
  }
  if (isSamsung && catLc.includes('phone')) {
    return [
      { key: 'gs', label: 'Galaxy S', img: getDeviceImage('Samsung Galaxy S', 'phones'), test: (n) => /galaxy\s*s\s*\d/.test(n) },
      { key: 'zflip', label: 'Galaxy Z Flip', img: getDeviceImage('Galaxy Z Flip', 'phones'), test: (n) => /z\s*flip/.test(n) },
      { key: 'zfold', label: 'Galaxy Z Fold', img: getDeviceImage('Galaxy Z Fold', 'phones'), test: (n) => /z\s*fold/.test(n) },
    ];
  }
  return null;
}

// sell-catalog/models). Tapping a model just fills the search box (setQuery). ───
export function DevicePicker({ onPickModel, onOther, onTap, resume }) {
  // Seed with the standard categories so the grid is never empty / "Other"-only.
  const [cats, setCats] = useState([...FALLBACK_CATS, ...META_CATS]);
  const [view, setView] = useState(resume?.brand ? 'models' : 'cats'); // cats | brands | subcats | models
  const [activeCat, setActiveCat] = useState(resume?.cat || null);
  const [brands, setBrands] = useState([]);
  const [activeBrand, setActiveBrand] = useState(resume?.brand || null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modelsError, setModelsError] = useState(false);
  const [brandsError, setBrandsError] = useState(false);
  // Monotonic request ids so a slow/late response for a previously-tapped brand/category
  // can never overwrite the grid for the one the customer is actually looking at now.
  const modelReqRef = useRef(0);
  const brandReqRef = useRef(0);
  const [subCats, setSubCats] = useState(null);   // Apple iPad/MacBook sub-lines
  const [subFilter, setSubFilter] = useState(null);
  const [yearFilter, setYearFilter] = useState(null);
  useEffect(() => { setYearFilter(null); }, [activeBrand, subFilter]);

  // When re-opened via the "Back" button after a model was picked, jump straight
  // back to that brand's model list instead of restarting at categories — and also
  // prefetch the sibling brands so the models→brands→categories back trail still works.
  useEffect(() => {
    if (!resume?.brand?.slug) return;
    let alive = true;
    const myReq = ++modelReqRef.current;
    setLoading(true); setModelsError(false);
    fetchJsonWithRetry(`${API_URL}/api/sell-catalog/models/${encodeURIComponent(resume.brand.slug)}?auto=1`)
      .then(d => {
        if (!alive || modelReqRef.current !== myReq) return;
        const list = (Array.isArray(d?.models) ? d.models : []).filter(m => m && m.name);
        if (list.length > 0) { setModels(list); setModelsError(false); }
        else { const fb = fallbackModels(resume.brand.slug); setModels(fb); setModelsError(fb.length === 0); }
      })
      .catch(() => {
        if (alive && modelReqRef.current === myReq) {
          const fb = fallbackModels(resume.brand.slug);
          setModels(fb); setModelsError(fb.length === 0);
        }
      })
      .finally(() => { if (alive && modelReqRef.current === myReq) setLoading(false); });
    if (resume?.cat?.slug) {
      fetchJsonWithRetry(`${API_URL}/api/products/brands?category_slug=${encodeURIComponent(resume.cat.slug)}`)
        .then(d => {
          if (!alive) return;
          const list = Array.isArray(d?.brands) ? d.brands : [];
          setBrands(list.length > 0 ? list : fallbackBrands(resume.cat.slug));
        })
        .catch(() => { if (alive) setBrands(fallbackBrands(resume.cat.slug)); });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh categories from the catalog; only override the fallback when the
  // API returns a real, non-empty list. On error/empty we keep the fallback.
  useEffect(() => {
    let alive = true;
    fetchJsonWithRetry(`${API_URL}/api/products/categories?type=sell`)
      .then(d => {
        if (!alive) return;
        const list = Array.isArray(d?.categories) ? d.categories.filter(c => c && c.slug) : [];
        if (list.length > 0) setCats([...list, ...META_CATS]);
      })
      .catch(() => { /* keep fallback categories */ });
    return () => { alive = false; };
  }, []);

  // Resilient loaders — timeout + auto-retry + stale-response guard, so a transient
  // network / cold-start / ingress blip on the live path can NEVER leave a blank grid.
  const loadBrands = async (cat) => {
    const myReq = ++brandReqRef.current;
    setBrands([]); setBrandsError(false); setLoading(true);
    try {
      const d = await fetchJsonWithRetry(`${API_URL}/api/products/brands?category_slug=${encodeURIComponent(cat.slug)}`, {}, { retries: 3, timeout: 7000, backoff: 500 });
      if (brandReqRef.current !== myReq) return;
      const list = Array.isArray(d?.brands) ? d.brands : [];
      if (list.length > 0) { setBrands(list); setBrandsError(false); }
      else { const fb = fallbackBrands(cat.slug); setBrands(fb); setBrandsError(fb.length === 0); }
    } catch {
      if (brandReqRef.current !== myReq) return;
      const fb = fallbackBrands(cat.slug);
      setBrands(fb); setBrandsError(fb.length === 0);
    } finally {
      if (brandReqRef.current === myReq) setLoading(false);
    }
  };

  const loadModels = async (brand) => {
    const myReq = ++modelReqRef.current;
    setModels([]); setModelsError(false); setLoading(true);
    try {
      const d = await fetchJsonWithRetry(`${API_URL}/api/sell-catalog/models/${encodeURIComponent(brand.slug)}?auto=1`, {}, { retries: 3, timeout: 7000, backoff: 500 });
      if (modelReqRef.current !== myReq) return;
      const list = (Array.isArray(d?.models) ? d.models : []).filter(m => m && m.name);
      if (list.length > 0) { setModels(list); setModelsError(false); }
      else { const fb = fallbackModels(brand.slug); setModels(fb); setModelsError(fb.length === 0); }
    } catch {
      if (modelReqRef.current !== myReq) return;
      const fb = fallbackModels(brand.slug);
      setModels(fb); setModelsError(fb.length === 0);
    } finally {
      if (modelReqRef.current === myReq) setLoading(false);
    }
  };

  const openCat = (cat) => {
    if (onTap) onTap();
    if (cat.other) { onOther && onOther(); return; }
    if (cat.meta) {
      // Meta devices have no catalog brands — jump straight to the curated model grid.
      setActiveCat(cat);
      setActiveBrand({ slug: cat.metaKind === 'glasses' ? 'meta-glasses' : 'meta-quest', name: cat.metaKind === 'glasses' ? 'Meta Glasses' : 'Meta Quest' });
      setSubCats(null); setSubFilter(null);
      setModels(cat.metaKind === 'glasses' ? META_GLASSES_MODELS : META_QUEST_MODELS); setModelsError(false); setLoading(false);
      setView('models');
      return;
    }
    setActiveCat(cat); setView('brands');
    loadBrands(cat);
  };

  const openBrand = (brand) => {
    if (onTap) onTap();
    setActiveBrand(brand);
    const sc = getSubCats(activeCat, brand);
    setSubCats(sc); setSubFilter(null);
    setView(sc ? 'subcats' : 'models');
    loadModels(brand);
  };

  const openSub = (sc) => {
    if (onTap) onTap();
    setSubFilter(sc); setView('models');
  };

  const color = activeCat ? (CAT_COLOR[activeCat.slug] || '#22d3ee') : '#22d3ee';

  const Spinner = () => (
    <div className="sg-loading" data-testid="device-picker-loading">
      <span className="sg-spin" /> Loading…
    </div>
  );

  // Shown whenever a picker fetch fails (or comes back empty) so the customer is never
  // stuck on a blank grid — one tap re-runs the resilient loader.
  const RetryBlock = ({ onRetry, testid }) => (
    <div className="sg-retry" data-testid={testid}>
      <p className="sg-retry-msg">Hmm, that didn't load. Please try again.</p>
      <button className="sg-retry-btn" onClick={onRetry} data-testid={`${testid}-btn`}>
        <RotateCw className="w-3.5 h-3.5" /> Retry
      </button>
    </div>
  );

  // ── MODELS VIEW ──
  if (view === 'models') {
    const lineModels = subFilter ? models.filter(m => subFilter.test((m.name || '').toLowerCase())) : models;
    const years = [...new Set(lineModels.map(m => m.year).filter(Boolean))].sort((a, b) => b - a);
    const shown = yearFilter ? lineModels.filter(m => m.year === yearFilter) : lineModels;
    return (
      <div className="mt-3" data-testid="device-examples">
        <button className="sg-back-btn" onClick={() => { if (onTap) onTap(); if (subFilter) { setSubFilter(null); setView('subcats'); } else if (activeCat?.meta) { setView('cats'); } else { setView('brands'); } }} data-testid="device-picker-back">
          <ChevronLeft className="w-3.5 h-3.5" /> {subFilter ? `${activeBrand?.name || ''} lines` : (activeCat ? `${catLabel(activeCat.name)} brands` : 'Back')}
        </button>
        <p className="sg-picker-heading">
          Tap your <span style={{ color }}>{subFilter?.label || activeBrand?.name || 'model'}</span>
        </p>
        {loading ? <Spinner /> : (modelsError ? (
          <RetryBlock testid="device-models-retry" onRetry={() => activeBrand && loadModels(activeBrand)} />
        ) : (
          <>
          {years.length > 1 && (
            <div className="sg-year-row" data-testid="device-year-filter">
              <span className="sg-year-label">Launch year:</span>
              {[null, ...years].map(y => (
                <button
                  key={y || 'all'}
                  onClick={() => setYearFilter(y)}
                  className={`sg-year-chip ${yearFilter === y ? 'is-active' : ''}`}
                  style={{ '--sg-c': color }}
                  data-testid={`device-year-filter-${y || 'all'}`}
                >
                  {y || 'All'}
                </button>
              ))}
            </div>
          )}
          <div className="sg-model-grid">
            {shown.map(m => (
              <button
                key={m.slug || m.name}
                onClick={() => onPickModel(m.name, { cat: activeCat, brand: activeBrand })}
                className="sg-model-btn sg-model-btn--img"
                style={{ '--sg-c': color }}
                data-testid={`suggestion-${(m.slug || m.name).toString().replace(/\s+/g, '-').toLowerCase()}`}
              >
                <img
                  src={getDeviceImage(m.name, activeCat?.slug)}
                  alt={m.name}
                  loading="lazy"
                  className="sg-model-img"
                />
                <span className="sg-model-name">{m.name}</span>
                {m.year ? <span className="sg-model-year">{m.year}</span> : null}
              </button>
            ))}
            <button onClick={() => onOther && onOther()} className="sg-model-btn sg-model-other" data-testid="device-model-not-listed">
              <HelpCircle className="w-3.5 h-3.5 inline mr-1" /> My model isn't listed
            </button>
          </div>
          </>
        ))}
      </div>
    );
  }

  // ── SUBCATEGORY VIEW (Apple iPad / MacBook sub-lines) ──
  if (view === 'subcats' && subCats) {
    return (
      <div className="mt-3" data-testid="device-subcategories">
        <button className="sg-back-btn" onClick={() => { if (onTap) onTap(); setView('brands'); }} data-testid="device-picker-back">
          <ChevronLeft className="w-3.5 h-3.5" /> {activeCat ? catLabel(activeCat.name) : 'Back'} brands
        </button>
        <p className="sg-picker-heading">
          Pick your <span style={{ color }}>{activeBrand?.name || 'Apple'}</span> line
        </p>
        <div className="sg-model-grid">
          {subCats.map(sc => (
            <button
              key={sc.key}
              onClick={() => openSub(sc)}
              className="sg-model-btn sg-model-btn--img"
              style={{ '--sg-c': color }}
              data-testid={`device-subcategory-${sc.key}`}
            >
              <img src={sc.img} alt={sc.label} loading="lazy" className="sg-model-img" />
              <span className="sg-model-name">{sc.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── BRANDS VIEW ──
  if (view === 'brands') {
    return (
      <div className="mt-3" data-testid="device-examples">
        <button className="sg-back-btn" onClick={() => { if (onTap) onTap(); setView('cats'); }} data-testid="device-picker-back">
          <ChevronLeft className="w-3.5 h-3.5" /> All devices
        </button>
        <p className="sg-picker-heading">
          Pick your <span style={{ color }}>{activeCat ? catLabel(activeCat.name) : 'device'}</span> brand
        </p>
        {loading ? <Spinner /> : (brandsError ? (
          <RetryBlock testid="device-brands-retry" onRetry={() => activeCat && loadBrands(activeCat)} />
        ) : (
          <div className="sg-brand-grid">
            {brands.map(b => (
              <button
                key={b.slug}
                onClick={() => openBrand(b)}
                className="sg-model-btn sg-brand-btn"
                style={{ '--sg-c': color }}
                data-testid={`device-brand-${b.slug}`}
              >
                <BrandLogo name={b.name} color={color} />
                <span className="sg-brand-name">{b.name}</span>
              </button>
            ))}
            {brands.length === 0 && (
              <button onClick={() => onOther && onOther()} className="sg-model-btn sg-model-other" data-testid="device-model-not-listed">
                <HelpCircle className="w-3.5 h-3.5 inline mr-1" /> Get a custom quote
              </button>
            )}
          </div>
        ))}
      </div>
    );
  }

  // ── CATEGORIES VIEW (default) ──
  return (
    <div className="mt-3" data-testid="device-examples">
      <p className="sg-picker-heading">
        <Sparkles className="w-3.5 h-3.5 inline mr-1 text-cyan-400" />
        Tap what you want to sell
      </p>
      {cats.length === 0 ? <Spinner /> : (
        <div className="sg-dtype-grid">
          {cats.map(cat => {
            const Icon = CAT_ICON[cat.icon] || HelpCircle;
            const c = CAT_COLOR[cat.slug] || '#64748b';
            return (
              <button
                key={cat.slug}
                onClick={() => openCat(cat)}
                className="sg-dtype-btn"
                style={{ '--sg-c': c }}
                data-testid={`device-type-${cat.slug.replace('-sell', '')}`}
              >
                <span className="sg-dtype-icon"><Icon className="w-5 h-5" /></span>
                <span className="sg-dtype-label">{catLabel(cat.name)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── All guidance-layer styles ───
export function GuideStyles() {
  return (
    <style>{`
      .sg-progress {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 10px 12px 4px;
      }
      .sg-step {
        display: flex;
        align-items: center;
        gap: 6px;
        opacity: 0.85;
        transition: opacity 0.3s;
      }
      .sg-step-active, .sg-step-done { opacity: 1; }
      .sg-step-num {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        font-size: 12px;
        font-weight: 800;
        color: #94a3b8;
        background: #f1f5f9;
        border: 1.5px solid #e2e8f0;
        flex-shrink: 0;
        transition: all 0.3s;
      }
      .sg-step-active .sg-step-num {
        color: #fff;
        background: linear-gradient(135deg, #10b981, #06b6d4);
        border-color: transparent;
        box-shadow: 0 0 0 4px rgba(6,182,212,0.15);
        animation: sg-pop 0.3s ease;
      }
      .sg-step-done .sg-step-num {
        color: #fff;
        background: #10b981;
        border-color: transparent;
      }
      .sg-step-label {
        font-size: 11px;
        font-weight: 700;
        color: #0f172a;
        white-space: nowrap;
      }
      .sg-step:not(.sg-step-active):not(.sg-step-done) .sg-step-label { color: #475569; }
      .sg-connector {
        width: 16px;
        height: 2px;
        border-radius: 2px;
        background: rgba(71,85,105,0.6);
        transition: background 0.3s;
      }
      .sg-connector-done { background: #10b981; }
      @media (max-width: 480px) {
        .sg-step-label { display: none; }
        .sg-connector { width: 24px; }
        .sg-progress { gap: 2px; }
      }
      @keyframes sg-pop {
        0% { transform: scale(0.6); }
        60% { transform: scale(1.15); }
        100% { transform: scale(1); }
      }

      /* Coach bubble */
      .sg-coach {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin: 6px 12px 2px;
        padding: 2px 0;
      }
      .sg-coach-avatar {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: linear-gradient(135deg, #10b981, #06b6d4);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        box-shadow: 0 0 14px -2px rgba(16,185,129,0.55);
      }
      .sg-coach-bubble {
        position: relative;
        background: #f0fdf4;
        border: 1.5px solid rgba(16,185,129,0.3);
        border-left: 3px solid #10b981;
        border-radius: 4px 14px 14px 14px;
        padding: 10px 14px;
        font-size: 13px;
        line-height: 1.5;
        font-weight: 600;
        color: #0f172a;
        box-shadow: 0 4px 14px -10px rgba(16,185,129,0.4);
        animation: sg-bubble-in 0.35s ease;
      }
      @keyframes sg-bubble-in {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }

      /* Point-here arrow */
      .sg-pointer {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        margin: 4px auto 6px;
      }
      .sg-pointer-text {
        font-size: 12px;
        font-weight: 700;
        color: #22d3ee;
      }
      .sg-pointer-arrow {
        color: #22d3ee;
        animation: sg-bounce-down 1.1s ease-in-out infinite;
      }
      @keyframes sg-bounce-down {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(5px); }
      }

      /* Device picker heading */
      .sg-picker-heading {
        font-size: 12px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #475569;
        margin-bottom: 10px;
      }

      /* Device type grid */
      .sg-dtype-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 8px;
      }
      @media (max-width: 640px) {
        .sg-dtype-grid { grid-template-columns: repeat(2, 1fr); }
      }
      .sg-dtype-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 16px 6px;
        border-radius: 16px;
        border: 1.5px solid rgba(15,23,42,0.08);
        background: #ffffff;
        box-shadow: 0 1px 3px rgba(15,23,42,0.05);
        transition: transform 0.15s, border-color 0.2s, background 0.2s, box-shadow 0.2s;
        min-height: 96px;
      }
      .sg-dtype-btn:hover {
        transform: translateY(-2px);
        border-color: color-mix(in srgb, var(--sg-c) 45%, transparent);
        background: color-mix(in srgb, var(--sg-c) 5%, #ffffff);
        box-shadow: 0 10px 24px -14px var(--sg-c);
      }
      .sg-dtype-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        border-radius: 14px;
        color: var(--sg-c);
        background: color-mix(in srgb, var(--sg-c) 13%, #ffffff);
      }
      .sg-dtype-label {
        font-size: 13px;
        font-weight: 700;
        color: #1e293b;
        text-align: center;
        line-height: 1.15;
        letter-spacing: 0.01em;
      }

      /* Model / brand grid */
      .sg-model-grid, .sg-brand-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
      }
      .sg-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 22px 0;
        font-size: 12.5px;
        font-weight: 600;
        color: #64748b;
      }
      .sg-spin {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(148,163,184,0.35);
        border-top-color: #22d3ee;
        border-radius: 50%;
        display: inline-block;
        animation: sg-spin 0.8s linear infinite;
      }
      @keyframes sg-spin { to { transform: rotate(360deg); } }
      .sg-retry {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        padding: 22px 12px;
        text-align: center;
      }
      .sg-retry-msg {
        margin: 0;
        font-size: 13px;
        font-weight: 600;
        color: #64748b;
      }
      .sg-retry-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 9px 18px;
        font-size: 13px;
        font-weight: 700;
        color: #0f172a;
        background: #22d3ee;
        border: none;
        border-radius: 9999px;
        cursor: pointer;
        transition: background-color 0.2s ease, transform 0.1s ease;
      }
      .sg-retry-btn:hover { background: #06b6d4; }
      .sg-retry-btn:active { transform: scale(0.96); }

      .sg-model-btn {
        padding: 14px 14px;
        border-radius: 13px;
        border: 1.5px solid color-mix(in srgb, var(--sg-c, #10b981) 28%, #e2e8f0);
        background: #ffffff;
        color: #1e293b;
        font-size: 13px;
        font-weight: 700;
        text-align: left;
        box-shadow: 0 1px 3px rgba(15,23,42,0.04);
        transition: transform 0.15s, border-color 0.2s, background 0.2s, box-shadow 0.2s;
        min-height: 50px;
      }
      .sg-model-btn:hover {
        transform: translateY(-2px);
        border-color: var(--sg-c, #10b981);
        background: color-mix(in srgb, var(--sg-c, #10b981) 7%, #ffffff);
        box-shadow: 0 8px 20px -12px var(--sg-c, #10b981);
      }
      .sg-model-btn--img {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
      }
      .sg-model-img {
        width: 38px;
        height: 38px;
        flex-shrink: 0;
        object-fit: contain;
        border-radius: 8px;
        background: color-mix(in srgb, var(--sg-c, #10b981) 6%, #f1f5f9);
      }
      .sg-model-name {
        flex: 1;
        min-width: 0;
        line-height: 1.25;
      }
      .sg-year-row { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin: 4px 0 10px; }
      .sg-year-label { font-size: 12px; font-weight: 600; color: #475569; margin-right: 2px; }
      .sg-year-chip {
        font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 999px;
        border: 1px solid #cbd5e1; background: #fff; color: #334155;
        transition: background-color .15s, color .15s, border-color .15s;
      }
      .sg-year-chip:hover { border-color: var(--sg-c, #10b981); }
      .sg-year-chip.is-active { background: var(--sg-c, #10b981); border-color: var(--sg-c, #10b981); color: #0f172a; }
      @media (max-width: 640px) {
        .sg-year-row { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 4px; scrollbar-width: none; }
        .sg-year-label, .sg-year-chip { flex-shrink: 0; }
        .sg-year-chip { min-height: 32px; min-width: 0; }
        .sg-model-grid .sg-model-year { display: none; }
      }
      .sg-model-year {
        flex-shrink: 0;
        font-size: 11px;
        font-weight: 700;
        color: #64748b;
        background: color-mix(in srgb, var(--sg-c, #10b981) 8%, #f1f5f9);
        border-radius: 6px;
        padding: 2px 7px;
        margin-left: 6px;
      }
      .sg-model-other {
        grid-column: 1 / -1;
        text-align: center;
        color: #64748b;
        font-weight: 600;
        border-style: dashed;
        border-color: rgba(100,116,139,0.35) !important;
        background: #f8fafc !important;
      }
      .sg-model-other:hover {
        color: #334155;
        border-color: rgba(100,116,139,0.5) !important;
        box-shadow: none !important;
      }
      .sg-back-btn {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 11px;
        font-weight: 700;
        color: #64748b;
        margin-bottom: 10px;
        transition: color 0.2s;
      }
      .sg-back-btn:hover { color: #059669; }

      /* Brand buttons with logos */
      .sg-brand-btn {
        display: flex;
        align-items: center;
        gap: 11px;
      }
      .sg-brand-logo {
        width: 22px;
        height: 22px;
        object-fit: contain;
        flex-shrink: 0;
      }
      .sg-brand-letter {
        width: 24px;
        height: 24px;
        border-radius: 7px;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 800;
        color: #ffffff;
      }
      .sg-brand-name {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      @media (max-width: 640px) {
        .sg-brand-name { white-space: normal; overflow-wrap: anywhere; line-height: 1.2; }
      }
    `}</style>
  );
}
